import { createMemoryCache } from "./createMemoryCache";
import {
  RESORT_CACHE_CONFIG,
  getResourceCachePolicy,
  setResortCacheEnabled,
  setResourceCacheEnabled,
  setResourceCacheTtlMs,
} from "./resortCache.config";
import { RESORT_CACHE_PREFIX, resortCacheKeys } from "./resortCache.keys";

/**
 * Shared resort memory cache instance.
 * Resource policy (enable + TTL) is read at call time so config changes apply live.
 */
export const resortMemoryCache = createMemoryCache({
  enabled: RESORT_CACHE_CONFIG.enabled,
  defaultTtlMs: RESORT_CACHE_CONFIG.defaultTtlMs,
});

/**
 * Fetch-through helper bound to a named resource policy.
 */
export const cachedFetch = (resourceKey, key, fetcher) => {
  const policy = getResourceCachePolicy(resourceKey);

  // Keep the underlying store's master flag in sync with config.
  if (resortMemoryCache.isEnabled() !== RESORT_CACHE_CONFIG.enabled) {
    resortMemoryCache.setEnabled(RESORT_CACHE_CONFIG.enabled);
  }

  return resortMemoryCache.getOrFetch(key, fetcher, {
    enabled: policy.enabled,
    ttlMs: policy.ttlMs,
  });
};

export const invalidateResortCache = {
  hotels: () => resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.hotels),

  hotelRooms: () =>
    resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.hotelRooms),

  roomPlans: (roomId) => {
    if (roomId) {
      resortMemoryCache.remove(resortCacheKeys.roomPlans(roomId));
      return;
    }
    resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.roomPlans);
  },

  reservations: () =>
    resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.reservations),

  availability: () => {
    resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.availability);
    // Reservations are an input to availability — drop them together.
    resortMemoryCache.removeByPrefix(RESORT_CACHE_PREFIX.reservations);
  },

  afterHotelChange: () => {
    invalidateResortCache.hotels();
    invalidateResortCache.availability();
  },

  afterHotelRoomChange: () => {
    invalidateResortCache.hotelRooms();
    invalidateResortCache.availability();
  },

  afterRoomPlanChange: (roomId) => {
    invalidateResortCache.roomPlans(roomId);
    invalidateResortCache.availability();
  },

  afterReservationChange: () => {
    invalidateResortCache.availability();
  },

  /** Broad invalidation when unsure which source changed */
  afterAvailabilitySourceChange: () => {
    invalidateResortCache.hotels();
    invalidateResortCache.hotelRooms();
    invalidateResortCache.roomPlans();
    invalidateResortCache.availability();
  },

  /** Nuclear option */
  all: () => resortMemoryCache.clear(),
};

export {
  RESORT_CACHE_CONFIG,
  resortCacheKeys,
  RESORT_CACHE_PREFIX,
  getResourceCachePolicy,
  setResortCacheEnabled,
  setResourceCacheEnabled,
  setResourceCacheTtlMs,
};
